import {Formik, FormikHelpers as FormikActions} from 'formik';
import React, {useRef, useState} from 'react';
import {Button} from '~/components/Button';
import {CheckboxInput} from '~/components/Controls/Checkbox';
import {EditorInput} from '~/components/Controls/Editor';
import {Input, InputType} from '~/components/Controls/Input';
import {Form, FormColumn, FormControls, FormRow, FormTitle, IntentText} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {MultiSelectInput} from '~/components/Controls/Select';
import {useAgencyBiddersQuery} from '~/modules/Clients/queries';
import {IBidder} from '~/modules/Bidders/models/Bidder';
import {Intent} from '~/lib/intent';
import {ID} from '~/lib/model';
import {useSourcesAllQuery} from '~/modules/Sources/queries';
import {TableSortDirection} from '~/components/Table';
import {sortByNumber} from '~/lib/sort';
import style from '~/modules/Clients/forms/index.module.scss';

function nextPaasFee(enabled: boolean, current: string): string {
    if (!enabled) return '';
    return current;
}

interface IBidderTitleReplace {
    from: string;
    to: string;
}

interface ICopyAgencyFormValues {
    patternReplaces: IBidderTitleReplace[];
    bidderReplaces: IBidderTitleReplace[];
    sources: number[];
    p2pCoefficient: string;
    p2pPaas: boolean;
    p2pReversedPayment: boolean;
    p2pPaasFee: string;
    activateBiddersAfterUpdate: boolean;
    rawJson: string;
}

interface ICopyAgencyFormProps {
    agencyId: ID;
    onSubmit?: (
        body: Record<string, unknown>,
        formikActions: FormikActions<ICopyAgencyFormValues>,
        close: IModalComponentClose
    ) => void;
}

function applyPatterns(title: string, patterns: IBidderTitleReplace[]): string {
    return patterns.filter(p => p.from).reduce((s, p) => s.split(p.from).join(p.to), title);
}

function buildBody(values: Omit<ICopyAgencyFormValues, 'rawJson'>): Record<string, unknown> {
    const patternRules = values.patternReplaces
        .filter(r => r.from)
        .map(r => ({op: 'replace', from: r.from, to: r.to}));

    const individualRules = values.bidderReplaces
        .filter(r => {
            if (!r.to || r.to === r.from) return false;
            return r.to !== applyPatterns(r.from, values.patternReplaces);
        })
        .map(r => ({
            op: 'replace',
            from: applyPatterns(r.from, values.patternReplaces),
            to: r.to,
        }));

    const p2p = values.p2pCoefficient.trim();
    const p2pNumber = p2p === '' ? null : Number(p2p);

    const paasFee = values.p2pPaasFee.trim();
    const paasFeeNumber = paasFee === '' ? null : Number(paasFee);

    return {
        modifications: {
            bidderTitle: [...patternRules, ...individualRules],
            ...(values.sources.length > 0 && {targeting: {sources: values.sources.map(String)}}),
            ...(p2pNumber !== null && Number.isFinite(p2pNumber) && {p2pCoefficient: p2pNumber}),
            p2pReversedPayment: values.p2pReversedPayment,
            ...(values.p2pPaas && {
                p2pPaas: true,
                ...(paasFeeNumber !== null && Number.isFinite(paasFeeNumber) && {p2pPaasFee: paasFeeNumber}),
            }),
        },
        activateBiddersAfterUpdate: values.activateBiddersAfterUpdate,
    };
}

function toJson(values: Omit<ICopyAgencyFormValues, 'rawJson'>): string {
    return JSON.stringify(buildBody(values), null, 2);
}

function fromJson(json: string, bidders: IBidder[]): Omit<ICopyAgencyFormValues, 'rawJson'> | null {
    try {
        const p = JSON.parse(json);
        const allRules: IBidderTitleReplace[] = (p.modifications?.bidderTitle ?? []).map((r: any) => ({
            from: r.from ?? '',
            to: r.to ?? '',
        }));
        const bidderTitles = new Set(bidders.map(b => b.title));
        const patternReplaces = allRules.filter(r => !bidderTitles.has(r.from));
        return {
            patternReplaces,
            bidderReplaces: bidders.map(b => {
                const exactRule = allRules.find(r => r.from === b.title);
                if (exactRule) return {from: b.title, to: exactRule.to};
                return {from: b.title, to: applyPatterns(b.title, patternReplaces)};
            }),
            sources: (p.modifications?.targeting?.sources ?? []).map(Number).filter(Boolean),
            p2pCoefficient: p.modifications?.p2pCoefficient != null ? String(p.modifications.p2pCoefficient) : '',
            p2pPaas: p.modifications?.p2pPaas ?? false,
            p2pReversedPayment: p.modifications?.p2pReversedPayment ?? false,
            p2pPaasFee: p.modifications?.p2pPaasFee != null ? String(p.modifications.p2pPaasFee) : '',
            activateBiddersAfterUpdate: p.activateBiddersAfterUpdate ?? false,
        };
    } catch {
        return null;
    }
}

export const CopyAgencyForm: IModalComponent<ICopyAgencyFormProps> = (props) => {
    const {data: bidders, isLoading} = useAgencyBiddersQuery(props.agencyId);
    const {data: sources = [], isLoading: isSourcesLoading} = useSourcesAllQuery();
    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [showJson, setShowJson] = useState(false);
    const baseReplacesRef = useRef<IBidderTitleReplace[] | null>(null);

    if (isLoading || !bidders) {
        return <Progress/>;
    }

    const initialValues: ICopyAgencyFormValues = {
        patternReplaces: [],
        bidderReplaces: bidders.map(b => ({from: b.title, to: b.title})),
        sources: [],
        p2pCoefficient: '',
        p2pPaas: false,
        p2pReversedPayment: false,
        p2pPaasFee: '',
        activateBiddersAfterUpdate: false,
        rawJson: '',
    };
    initialValues.rawJson = toJson(initialValues);

    return (
        <Formik<ICopyAgencyFormValues>
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                try {
                    props.onSubmit?.(JSON.parse(values.rawJson), actions, props.close);
                } catch {
                    actions.setStatus('Invalid JSON');
                    actions.setSubmitting(false);
                }
            }}
        >
            {({values, isSubmitting, handleSubmit, status, setValues}) => {
                const updateForm = (patch: Partial<Omit<ICopyAgencyFormValues, 'rawJson'>>) => {
                    const next = {...values, ...patch};
                    setValues({...next, rawJson: toJson(next)});
                };

                const handleJsonChange = (newJson: string) => {
                    const parsed = fromJson(newJson, bidders);
                    setValues(parsed ? {...parsed, rawJson: newJson} : {...values, rawJson: newJson});
                };

                return (
                    <Form onSubmit={handleSubmit}>
                        <FormTitle>
                            Clone Agency
                            <a
                                onClick={() => setShowJson(v => !v)}
                                style={{fontSize: 13, fontWeight: 400, marginLeft: 12, cursor: 'pointer', color: 'var(--accent-primary)'}}
                            >
                                <i className="fa fa-code" style={{marginRight: 4}}/>
                                {showJson ? 'Hide JSON' : 'Show JSON'}
                            </a>
                        </FormTitle>
                        {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}

                        <FormRow>
                            <FormColumn>
                                <FormRow caption="Bidder title replacements" noBorder>
                                    <FormColumn noBorder noMargin>
                                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8}}>
                                            <Input
                                                value={findText}
                                                placeholder="Find in all titles..."
                                                disabled={isSubmitting}
                                                onChange={val => {
                                                    if (!val) {
                                                        if (baseReplacesRef.current) {
                                                            updateForm({patternReplaces: [], bidderReplaces: baseReplacesRef.current});
                                                            baseReplacesRef.current = null;
                                                        }
                                                        setFindText('');
                                                        return;
                                                    }
                                                    if (!findText) {
                                                        baseReplacesRef.current = values.bidderReplaces;
                                                    }
                                                    setFindText(val);
                                                    const base = baseReplacesRef.current!;
                                                    updateForm({
                                                        patternReplaces: [{from: val, to: replaceText}],
                                                        bidderReplaces: base.map(r => ({
                                                            ...r,
                                                            to: r.to.split(val).join(replaceText),
                                                        })),
                                                    });
                                                }}
                                            />
                                            <Input
                                                value={replaceText}
                                                placeholder="Replace with..."
                                                disabled={isSubmitting || !findText}
                                                onChange={val => {
                                                    setReplaceText(val);
                                                    if (findText && baseReplacesRef.current) {
                                                        updateForm({
                                                            patternReplaces: [{from: findText, to: val}],
                                                            bidderReplaces: baseReplacesRef.current.map(r => ({
                                                                ...r,
                                                                to: r.to.split(findText).join(val),
                                                            })),
                                                        });
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                            {values.bidderReplaces.map((r, i) => (
                                                <div key={i} style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
                                                    <Input value={r.from} disabled/>
                                                    <Input
                                                        value={r.to}
                                                        placeholder="New title"
                                                        disabled={isSubmitting}
                                                        onChange={val => updateForm({
                                                            bidderReplaces: values.bidderReplaces.map((x, idx) =>
                                                                idx === i ? {...x, to: val} : x
                                                            ),
                                                        })}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </FormColumn>
                                </FormRow>

                                <FormRow caption="Replace sources in pretargeting" noBorder>
                                    <FormColumn noBorder noMargin>
                                        <MultiSelectInput
                                            value={values.sources}
                                            disabled={isSubmitting || isSourcesLoading}
                                            rightIcon={isSourcesLoading && <i className="fa fa-spinner fa-spin"/>}
                                            searchable
                                            onChange={sources => updateForm({sources})}
                                            items={sortByNumber(
                                                sources.filter(s => s.name).map(s => ({label: `${s.id} · ${s.name}`, value: s.id})),
                                                'value',
                                                TableSortDirection.ASC
                                            )}
                                        />
                                    </FormColumn>
                                </FormRow>

                                <FormRow caption="P2P Coefficient" noBorder>
                                    <FormColumn noBorder noMargin>
                                        <Input
                                            value={values.p2pCoefficient}
                                            type={InputType.FLOAT}
                                            placeholder="Leave empty to keep original value"
                                            disabled={isSubmitting}
                                            min={0}
                                            max={100}
                                            step="any"
                                            onChange={val => updateForm({p2pCoefficient: val ?? ''})}
                                        />
                                    </FormColumn>
                                </FormRow>

                                <FormRow noBorder>
                                    <FormColumn noBorder noMargin>
                                        <button
                                            type="button"
                                            disabled={isSubmitting || values.p2pReversedPayment}
                                            className={style.checkboxToggleButton}
                                            onClick={() =>
                                                updateForm({
                                                    p2pPaas: !values.p2pPaas,
                                                    p2pPaasFee: nextPaasFee(!values.p2pPaas, values.p2pPaasFee),
                                                })
                                            }
                                        >
                                            <CheckboxInput
                                                value={values.p2pPaas}
                                                disabled={isSubmitting || values.p2pReversedPayment}
                                                onChange={v => updateForm({p2pPaas: v, p2pPaasFee: nextPaasFee(v, values.p2pPaasFee)})}
                                            />
                                            P2P PaaS
                                        </button>
                                    </FormColumn>
                                </FormRow>

                                <FormRow noBorder>
                                    <FormColumn noBorder noMargin>
                                        <button
                                            type="button"
                                            disabled={isSubmitting || values.p2pPaas}
                                            className={style.checkboxToggleButton}
                                            onClick={() => updateForm({p2pReversedPayment: !values.p2pReversedPayment})}
                                        >
                                            <CheckboxInput
                                                value={values.p2pReversedPayment}
                                                disabled={isSubmitting || values.p2pPaas}
                                                onChange={v => updateForm({p2pReversedPayment: v})}
                                            />
                                            Is Reverse Payment
                                        </button>
                                    </FormColumn>
                                </FormRow>

                                <FormRow caption="P2P PaaS Fee %" noBorder>
                                    <FormColumn noBorder noMargin>
                                        <Input
                                            value={values.p2pPaasFee}
                                            type={InputType.FLOAT}
                                            placeholder="P2P PaaS Fee %"
                                            disabled={isSubmitting || !values.p2pPaas}
                                            min={0}
                                            max={10}
                                            step="any"
                                            onChange={val => updateForm({p2pPaasFee: val ?? ''})}
                                        />
                                    </FormColumn>
                                </FormRow>

                                <FormRow noBorder>
                                    <FormColumn noBorder noMargin>
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            className={style.checkboxToggleButton}
                                            onClick={() => updateForm({activateBiddersAfterUpdate: !values.activateBiddersAfterUpdate})}
                                        >
                                            <CheckboxInput
                                                toggle
                                                value={values.activateBiddersAfterUpdate}
                                                disabled={isSubmitting}
                                                onChange={v => updateForm({activateBiddersAfterUpdate: v})}
                                            />
                                            Activate bidders after update
                                        </button>
                                    </FormColumn>
                                </FormRow>
                            </FormColumn>

                            {showJson && (
                                <FormColumn>
                                    <EditorInput
                                        value={values.rawJson}
                                        mode="json"
                                        disabled={isSubmitting}
                                        canFullScreen
                                        onChange={handleJsonChange}
                                    />
                                </FormColumn>
                            )}
                        </FormRow>

                        <FormControls>
                            <Button onClick={props.close} disabled={isSubmitting}>Cancel</Button>
                            <Button
                                type="submit"
                                intent={Intent.PRIMARY}
                                disabled={isSubmitting}
                                rightIcon={isSubmitting && <i className="fa fa-spinner fa-spin"/>}
                            >
                                Clone
                            </Button>
                        </FormControls>
                    </Form>
                );
            }}
        </Formik>
    );
};
